/**
 * Cart bundle base-remove confirm (TSO cart reskin, Task 5).
 *
 * Removing a build's BASE line (data-bundle-remove, added by
 * snippets/cart-line-row.liquid ONLY to the base row's remove control —
 * accessory/single removes are untouched and stay Dawn's native one-click)
 * shows a themed confirm dialog, then removes the base + every add-on line
 * in the same .tso-cart-bundle (snippets/cart-bundle-group.liquid) in ONE
 * atomic /cart/update.js keyed by line key (never index), reconciles the
 * server-authoritative custom-color fee, then re-renders Dawn's cart UI.
 *
 * Dialog markup reuses Task 1's shipped CSS verbatim: .tso-cart-confirm
 * (fixed scrim, z-index 1100 — above Dawn's .drawer at 1000) wrapping a
 * single child "card" (.tso-cart-confirm > *) with h2/p text and
 * .cpb-btn/.cpb-btn--primary/.cpb-btn--ghost actions (assets/tso-cart.css).
 *
 * Fee coordination (snippets/custom-color-fee.liquid): our /cart/update.js
 * carries the 'x-ccf-reconcile' header so the global fetch-wrap there does
 * NOT also schedule a reconcile (it skips writes carrying that header) —
 * we call window.reconcileCustomColorFee() ourselves once the removal
 * settles, then re-render <cart-items>/<cart-drawer-items> explicitly so
 * the list updates even on the (idempotent, common) case where the fee
 * didn't change.
 */
(function () {
  if (window.__cartBundle) return;
  window.__cartBundle = true;

  var RECON_HEADER = 'x-ccf-reconcile';

  var activeOverlay = null;
  var activeTrigger = null;

  /**
   * Full Dawn-equivalent re-render after the atomic removal + fee reconcile.
   * onCartUpdate() alone is NOT enough: on the cart page it only swaps the
   * item list — footer total, header cart-icon bubble and the is-empty
   * classes all go stale (audit P0-5). Instead: one bundled-section fetch
   * (post-reconcile, so the fee line is settled), swapped through each
   * element's own getSectionsToRender()/getSectionInnerHTML(), then sync
   * the server-rendered empty state onto the live wrappers.
   */
  function refreshCartUi() {
    var els = Array.prototype.slice.call(document.querySelectorAll('cart-items, cart-drawer-items'));
    var renderable = els.filter(function (el) {
      return typeof el.getSectionsToRender === 'function' && typeof el.getSectionInnerHTML === 'function';
    });

    function fallback() {
      els.forEach(function (el) {
        if (el && typeof el.onCartUpdate === 'function') el.onCartUpdate();
      });
    }

    var sections = [];
    renderable.forEach(function (el) {
      el.getSectionsToRender().forEach(function (s) {
        if (sections.indexOf(s.section) === -1) sections.push(s.section);
      });
    });
    if (!sections.length) {
      fallback();
      return;
    }

    fetch(window.location.pathname + '?sections=' + sections.join(','))
      .then(function (res) {
        if (!res.ok) throw new Error('sections fetch failed');
        return res.json();
      })
      .then(function (rendered) {
        renderable.forEach(function (el) {
          el.getSectionsToRender().forEach(function (s) {
            if (!rendered[s.section]) return;
            var container = document.getElementById(s.id);
            if (!container) return;
            var target = container.querySelector(s.selector) || container;
            target.innerHTML = el.getSectionInnerHTML(rendered[s.section], s.selector);
          });
        });

        // Empty state: read it off the server-rendered markup (cart-items /
        // cart-drawer roots carry is-empty when cart == empty) and mirror it
        // the way Dawn's updateQuantity does.
        var isEmpty = null;
        sections.some(function (name) {
          if (!rendered[name]) return false;
          var root = new DOMParser()
            .parseFromString(rendered[name], 'text/html')
            .querySelector('cart-items, cart-drawer');
          if (!root) return false;
          isEmpty = root.classList.contains('is-empty');
          return true;
        });
        if (isEmpty === null) return;
        renderable.forEach(function (el) {
          el.classList.toggle('is-empty', isEmpty);
        });
        var cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
        var drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.classList.toggle('is-empty', isEmpty);
      })
      .catch(fallback);
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeConfirm();
    }
  }

  function closeConfirm() {
    if (!activeOverlay) return;
    activeOverlay.parentNode && activeOverlay.parentNode.removeChild(activeOverlay);
    document.removeEventListener('keydown', onKeydown, true);
    var trigger = activeTrigger;
    activeOverlay = null;
    activeTrigger = null;
    if (trigger && typeof trigger.focus === 'function') {
      // trigger row may itself be gone (removed on confirm) — guard the focus call
      try { trigger.focus(); } catch (e) {}
    }
  }

  function removeBundle(bundle) {
    var keys = Array.prototype.map
      .call(bundle.querySelectorAll('[data-line-key]'), function (el) {
        return el.getAttribute('data-line-key');
      })
      .filter(Boolean);

    if (!keys.length) {
      closeConfirm();
      return;
    }

    var updates = {};
    keys.forEach(function (key) {
      updates[key] = 0;
    });

    var headers = { 'Content-Type': 'application/json' };
    headers[RECON_HEADER] = '1';

    fetch('/cart/update.js', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart update ' + res.status);
        return window.reconcileCustomColorFee ? window.reconcileCustomColorFee() : null;
      })
      .then(function () {
        refreshCartUi();
      })
      .catch(function () {
        // best-effort re-render even on a failed reconcile so the cart
        // doesn't look stale if /cart/update.js itself succeeded
        refreshCartUi();
      })
      .finally(function () {
        closeConfirm();
      });
  }

  function openConfirm(bundle, trigger) {
    if (activeOverlay) closeConfirm();
    activeTrigger = trigger;

    var overlay = document.createElement('div');
    overlay.className = 'tso-cart-confirm';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'tso-cart-confirm-title');

    var strings = window.tsoCartStrings || {};
    var card = document.createElement('div');
    card.innerHTML =
      '<h2 id="tso-cart-confirm-title">' + (strings.bundleRemoveTitle || 'Remove this build?') + '</h2>' +
      '<p>' + (strings.bundleRemoveBody || 'This removes the base and all its add-ons.') + '</p>' +
      '<div class="tso-cart-confirm__actions">' +
      '<button type="button" class="cpb-btn cpb-btn--ghost" data-cc-cancel>' + (strings.bundleRemoveCancel || 'Cancel') + '</button>' +
      '<button type="button" class="cpb-btn cpb-btn--primary" data-cc-ok>' + (strings.bundleRemoveConfirm || 'Remove build') + '</button>' +
      '</div>';
    overlay.appendChild(card);

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeConfirm(); // scrim click
    });
    card.querySelector('[data-cc-cancel]').addEventListener('click', closeConfirm);
    card.querySelector('[data-cc-ok]').addEventListener('click', function () {
      removeBundle(bundle);
    });

    document.body.appendChild(overlay);
    document.addEventListener('keydown', onKeydown, true);
    activeOverlay = overlay;
    // initial focus on Cancel — an accidental Enter must not destroy the build;
    // Tab cycles between the two buttons (minimal trap for the 2-control dialog)
    var cancelBtn = card.querySelector('[data-cc-cancel]');
    var okBtn = card.querySelector('[data-cc-ok]');
    cancelBtn.focus();
    overlay.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      e.preventDefault();
      (document.activeElement === cancelBtn ? okBtn : cancelBtn).focus();
    });
  }

  // Capture-phase so this survives Dawn's section re-renders swapping the
  // row markup underneath any bubble-phase listeners Dawn itself attached.
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('[data-bundle-remove]');
      if (!btn) return;
      var bundle = btn.closest('.tso-cart-bundle');
      if (!bundle) return;
      e.preventDefault();
      e.stopPropagation();
      openConfirm(bundle, btn);
    },
    true
  );
})();
